import { twMerge } from 'tailwind-merge';
import { InfoTip } from '@/components/Help/InfoTip';
import type { HelpTip } from '@/docs/tips/types';

export interface TableHeaderProps {
  text?: string | React.ReactNode;
  sortable?: boolean;
  thClassName?: string;
  textClassName?: string;
  children?: React.ReactNode;
  onSortClick?: () => void;
  /** "i" icon explaining the column. */
  infoTip?: HelpTip;
}

/**
 * Label followed by its "i" icon. The last word and the icon can't be split,
 * so the icon always sits right after the last line of the label.
 */
const LabelWithInfoTip = ({
  text,
  infoTip,
}: {
  text: TableHeaderProps['text'];
  infoTip: HelpTip;
}) => {
  const icon = (
    // h-4 + align-top: centered on the line (leading-4)
    <span className="ml-1 inline-flex h-4 items-center align-top">
      <InfoTip tip={infoTip} size={13} />
    </span>
  );

  if (typeof text !== 'string') {
    return (
      <>
        {text}
        {icon}
      </>
    );
  }

  const label = text.trim();
  const lastSpaceIndex = label.lastIndexOf(' ');

  return (
    <>
      {label.slice(0, lastSpaceIndex + 1)}
      <span className="whitespace-nowrap">
        {label.slice(lastSpaceIndex + 1)}
        {icon}
      </span>
    </>
  );
};

export const TableHead = ({
  text,
  sortable,
  thClassName,
  textClassName,
  children,
  onSortClick,
  infoTip,
}: TableHeaderProps) => {
  return (
    <th
      className={twMerge(
        '!py-[12.5px] !rounded-none !bg-purple-F7F5FF select-none [-webkit-touch-callout:none] [-webkit-user-select:none] dark-mode:!bg-coal-200',
        sortable && 'hover:cursor-pointer',
        thClassName,
      )}
      onClick={sortable ? onSortClick : undefined}
    >
      {children ? (
        <>{children}</>
      ) : (
        <div className={twMerge('', sortable && 'sort')}>
          <span
            className={twMerge(
              'sort-label text-purple-5433FF text-sm font-normal leading-4 dark-mode:text-gray-600',
              // The "i" icon flows inline, right after the last word of the label
              infoTip && 'inline',
              textClassName,
            )}
          >
            {infoTip ? (
              <LabelWithInfoTip text={text} infoTip={infoTip} />
            ) : (
              text
            )}
          </span>
          {sortable && (
            <span className="sort-icon gap-[0.5px] before:w-[5.5px] before:h-[5.5px] after:w-[5.5px] after:h-[5.5px]"></span>
          )}
        </div>
      )}
    </th>
  );
};
